import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PeakBrandMark from "../components/onboarding/PeakBrandMark";
import appIcon from "../assets/download/peak-app-icon.png";
import appStoreBadge from "../assets/download/app-store-badge.png";
import googlePlayBadge from "../assets/download/google-play-badge.png";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=app.peak.peakandroid";
// No storefront in the path, so Apple opens the visitor's own country store.
const APP_STORE_URL = "https://apps.apple.com/app/peak/id6784633966";

const DownloadPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [params] = useSearchParams();
  const subscribed = params.get("subscription_success") === "true";
  const canceled = params.get("subscription_canceled") === "true";
  const existing = params.get("existing") === "1";

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-base-200 px-6 py-12">
      <div className="w-full max-w-lg rounded-md border border-base-300 bg-base-100 px-6 py-8 shadow-sm">
        <div className="flex items-center gap-4">
          <img
            src={appIcon}
            alt=""
            width={64}
            height={64}
            className="h-16 w-16 rounded-[22%] shadow-[0_4px_12px_rgba(0,90,122,0.25)]"
          />
          <PeakBrandMark enlarged />
        </div>
        <h1 className="mt-8 font-heading text-4xl tracking-tight">Download the app</h1>
        <div className="mt-3 flex flex-col gap-1 font-grotesk leading-relaxed text-base-content/80">
          {existing ? (
            <>
              <p>You already have a PEAK account.</p>
              <p>Download the app and sign in there.</p>
            </>
          ) : (
            <p>Your account is ready. Download and log in through the native app for the full experience.</p>
          )}
          {currentUser?.email && (
            <div className="mt-2 flex flex-col gap-1.5">
              <span className="text-xs font-medium text-base-content/60">Your account email</span>
              <p className="w-fit max-w-full truncate rounded-md border border-base-300 bg-base-200 px-3 py-1.5 text-sm font-medium text-base-content">
                {currentUser.email}
              </p>
            </div>
          )}
        </div>
        {subscribed && (
          <p className="mt-4 rounded-md bg-primary/10 px-4 py-3 text-sm font-grotesk">
            Your subscription is set up. Sign in with the same account in the app.
          </p>
        )}
        {canceled && (
          <p className="mt-4 rounded-md bg-base-200 px-4 py-3 text-sm font-grotesk">
            Checkout was canceled. You&apos;re on the free plan and can upgrade in the app.
          </p>
        )}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-[10px] transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <img src={appStoreBadge} alt="Download on the App Store" width={156} height={52} className="h-[52px] w-auto" />
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-[10px] transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <img src={googlePlayBadge} alt="Get it on Google Play" width={175} height={52} className="h-[52px] w-auto" />
          </a>
        </div>
        <div className="mt-8 flex items-center justify-between text-sm font-grotesk">
          <Link to="/" className="text-base-content/70 hover:text-base-content">
            Back to site
          </Link>
          {currentUser && (
            <button type="button" onClick={() => logout()} className="text-base-content/70 hover:text-base-content">
              Sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
